/*
 * PEARSON PROPRIETARY AND CONFIDENTIAL INFORMATION SUBJECT TO NDA
 * Copyright © 2025 Pearson Education, Inc.
 * All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Pearson Education, Inc. The intellectual and technical concepts contained
 * herein are proprietary to Pearson Education, Inc. and may be covered by U.S. and Foreign Patents,
 * patent applications, and are protected by trade secret or copyright law.
 * Dissemination of this information, reproduction of this material, and copying or distribution of this software
 * is strictly forbidden unless prior written permission is obtained
 * from Pearson Education, Inc.
 */

/**
 * Assignment Service
 * This service is used to create, get and delete assignments
 * @file AssignmentService.js
 * @author Satyaki Bose
 */
/* eslint-disable class-methods-use-this */

// eslint-disable-next-line max-classes-per-file
import Framework, { BaseService } from '@greenville/framework';
import { AET_STORE_EVENTS, ASSIGNMENT_GET_QUERY } from '../common/Constants';
import {
  mapRootAssessmentToEachQuestion,
  transformAssignmentResponse
} from '../utils/AssignmentUtils';
import { constructAssignmentPayload, extractAssignments } from '../utils/AssignmentsBridgeFormatAdapter';

class AssignmentOperations {
  static handleCreate(eventData, client) {
    const { contextId, assignments } = eventData;
    const apiAssignments = assignments.map(assignment => constructAssignmentPayload(assignment, contextId));
    return {
      promise: client.post(`/aet/courses/${contextId}/assignments`, { assignments: apiAssignments }),
      contextId,
      originalRequestData: eventData
    };
  }

  static handleGet(eventData, client) {
    const { contextId, studentId, assignmentStatus } = eventData;
    const parts = [];
    if (studentId) {
      parts.push(`studentId=${encodeURIComponent(studentId)}`);
    }
    if (assignmentStatus) {
      parts.push(`assignmentStatus=${encodeURIComponent(assignmentStatus)}`);
    }
    const query = parts.length ? `?${parts.join('&')}` : '';

    return {
      promise: client.get(`/aet/courses/${contextId}/assignments${query}`),
      contextId
    };
  }

  static handleDelete(eventData, client) {
    const {
      contextId,
      assignmentIds,
      redirectAfterDelete = false,
      totalAssignments,
      totalActiveAssignments
    } = eventData;
    return {
      promise: client.delete(`/aet/courses/${contextId}/assignments`, {
        data: { assignmentIds, totalAssignments, totalActiveAssignments }
      }),
      contextId,
      redirectAfterDelete
    };
  }

  static handleUpdate(eventData, client) {
    const { contextId, assignmentId, assignments, isInProgressUpdate = false } = eventData;
    const apiAssignments = isInProgressUpdate ? assignments : assignments.map(assignment => constructAssignmentPayload(assignment, contextId));

    return {
      promise: client.put(`/aet/courses/${contextId}/assignments/${assignmentId}`, { assignments: apiAssignments }),
      contextId
    };
  }

  static handleCreateSuccess(response, contextId, originalRequestData) {
    const assignments = response?.data?.assignments || [];
    Framework.getEventManager().publish(AET_STORE_EVENTS.ASSIGNMENT_CREATE_COMPLETED, {
      ...response,
      assignments,
      originalAssignments: originalRequestData?.assignments || [],
      totalUpdated: assignments.length,
      isSingleAssignmentCreate: originalRequestData.isSingleAssignmentCreate,
      isScheduleDraftSave: originalRequestData.isScheduleDraftSave,
      isAssistedScheduleFinish: originalRequestData.isAssistedScheduleFinish
    });
    Framework.getEventManager().publish(AET_STORE_EVENTS.ASSIGNMENT_GET_REQUESTED, {
      contextId,
      ...ASSIGNMENT_GET_QUERY.INSTRUCTOR
    });
  }

  static handleGetSuccess(response, eventData = {}) {
    const { studentId, applyToCourseAssignments = false } = eventData;
    const assignmentResponseList = response?.data?.assignments;
    const internalFormat = assignmentResponseList?.map(extractAssignments) || [];
    const transformedAssignments = internalFormat?.map(transformAssignmentResponse) || [];
    const mappedAssignments = mapRootAssessmentToEachQuestion(transformedAssignments);

    // student scoped response is a delta only, must not fully merge to course assignments store
    if (studentId) {
      // simply creates {assignmentId: dueDate} object from original response, used in utils
      const dueById = Object.fromEntries(
        mappedAssignments
          .map((assignment) => {
            const due = assignment.dueDate;
            if (!assignment.assignmentId || due == null || String(due).length === 0) {
              return null;
            }
            return [assignment.assignmentId, due];
          })
          .filter(Boolean)
      );
      const studentsRoster = Framework.getStoreRegistry().getStore('studentsRoster');
      const assignmentStore = Framework.getStoreRegistry().getStore('assignment');
      studentsRoster?.mergeAssignmentDueDatesForStudent(studentId, dueById);
      studentsRoster?.setStudentAssignments(studentId, mappedAssignments);
      if (applyToCourseAssignments) { // this is true only for student roles, they must see their own updated due dates
        assignmentStore?.mergeStudentEffectiveDueDates(dueById);
      }
      return;
    }

    Framework.getStoreRegistry()
      .getStore('assignment')
      .setAssignments(mappedAssignments);
    Framework.getEventManager().publish(AET_STORE_EVENTS.ASSIGNMENT_GET_COMPLETED, mappedAssignments);
  }

  static handleDeleteSuccess(response, contextId, redirectAfterDelete, storeRefresh) {
    Framework.getEventManager().publish(AET_STORE_EVENTS.ASSIGNMENT_DELETE_COMPLETED, {
      ...response,
      redirectAfterDelete
    });
    if (storeRefresh) {
      Framework.getEventManager().publish(AET_STORE_EVENTS.ASSIGNMENT_GET_REQUESTED, {
        contextId,
        ...ASSIGNMENT_GET_QUERY.INSTRUCTOR
      });
    }
  }

  static handleUpdateSuccess(response, contextId, originalRequestData) {
    const assignments = response?.data?.assignments || [];

    Framework.getEventManager().publish(AET_STORE_EVENTS.ASSIGNMENT_UPDATE_COMPLETED, {
      ...response,
      assignments,
      originalAssignments: originalRequestData?.assignments || [],
      totalUpdated: assignments.length
    });
    Framework.getEventManager().publish(AET_STORE_EVENTS.ASSIGNMENT_GET_REQUESTED, {
      contextId,
      ...ASSIGNMENT_GET_QUERY.INSTRUCTOR
    });
  }

  static handleBulkEdit(eventData, client) {
    const { contextId, assignments } = eventData;

    return {
      promise: client.put(`/aet/courses/${contextId}/assignments`, { assignments }),
      contextId
    };
  }

  static handleBulkEditSuccess(response, contextId, singleUpdate = false) {
    Framework.getEventManager().publish(AET_STORE_EVENTS.BULK_EDIT_COMPLETED, response, { singleUpdate });
    Framework.getEventManager().publish(AET_STORE_EVENTS.ASSIGNMENT_GET_REQUESTED, {
      contextId,
      ...ASSIGNMENT_GET_QUERY.INSTRUCTOR
    });
  }
}

export class AssignmentCreateService extends BaseService {
  constructor() {
    super(AET_STORE_EVENTS.ASSIGNMENT_CREATE_REQUESTED, 'assignmentCreateStatus');
    this.contextId = null;
    this.originalRequestData = null;
  }

  handleEvent(eventName, eventData) {
    const { contextId } = eventData;
    this.contextId = contextId;
    this.originalRequestData = eventData;
    return AssignmentOperations.handleCreate(eventData, this.getClient('marinV2')).promise;
  }

  handleResponse(response) {
    AssignmentOperations.handleCreateSuccess(response, this.contextId, this.originalRequestData);
  }

  handleError(error) {
    Framework.getEventManager().publish(AET_STORE_EVENTS.ASSIGNMENT_CREATE_FAILED, error);
    Framework.getEventManager().publish(AET_STORE_EVENTS.ASSIGNMENT_GET_REQUESTED, {
      contextId: this.contextId,
      ...ASSIGNMENT_GET_QUERY.INSTRUCTOR
    });
  }
}

export class AssignmentGetService extends BaseService {
  constructor() {
    super(AET_STORE_EVENTS.ASSIGNMENT_GET_REQUESTED, 'assignmentGetStatus');
  }

  handleEvent(eventName, eventData) {
    return AssignmentOperations.handleGet(eventData, this.getClient('marinV2')).promise;
  }

  handleResponse(response, eventData = {}) {
    AssignmentOperations.handleGetSuccess(response, eventData);
  }

  handleError(error) {
    Framework.getEventManager().publish(AET_STORE_EVENTS.ASSIGNMENT_GET_FAILED, error);
  }
}

export class AssignmentDeleteService extends BaseService {
  constructor() {
    super(AET_STORE_EVENTS.ASSIGNMENT_DELETE_REQUESTED, 'assignmentDeleteStatus');
    this.contextId = null;
    this.redirectAfterDelete = false;
    this.storeRefresh = true;
  }

  handleEvent(eventName, eventData) {
    const { contextId, redirectAfterDelete = false, storeRefresh = true } = eventData;
    this.contextId = contextId;
    this.redirectAfterDelete = redirectAfterDelete;
    this.storeRefresh = storeRefresh;
    return AssignmentOperations.handleDelete(eventData, this.getClient('marin')).promise;
  }

  handleResponse(response) {
    AssignmentOperations.handleDeleteSuccess(response, this.contextId, this.redirectAfterDelete, this.storeRefresh);
  }

  handleError(error) {
    Framework.getEventManager().publish(AET_STORE_EVENTS.ASSIGNMENT_DELETE_FAILED, error);
  }
}

export class AssignmentUpdateService extends BaseService {
  constructor() {
    super(AET_STORE_EVENTS.ASSIGNMENT_UPDATE_REQUESTED, 'assignmentUpdateStatus');
    this.contextId = null;
    this.originalRequestData = null;
  }

  handleEvent(eventName, eventData) {
    const { contextId } = eventData;
    this.contextId = contextId;
    this.originalRequestData = eventData;
    return AssignmentOperations.handleUpdate(eventData, this.getClient('marinV2')).promise;
  }

  handleResponse(response) {
    AssignmentOperations.handleUpdateSuccess(response, this.contextId, this.originalRequestData);
  }

  handleError(error) {
    Framework.getEventManager().publish(AET_STORE_EVENTS.ASSIGNMENT_UPDATE_FAILED, error);
  }
}

export class AssignmentBulkEditService extends BaseService {
  constructor() {
    super(AET_STORE_EVENTS.BULK_EDIT_REQUESTED, 'bulkEditRequested');
    this.contextId = null;
  }

  handleEvent(eventName, eventData) {
    const { contextId } = eventData;
    this.contextId = contextId;
    return AssignmentOperations.handleBulkEdit(eventData, this.getClient('marin')).promise;
  }

  handleResponse(response, eventData) {
    AssignmentOperations.handleBulkEditSuccess(response, this.contextId, eventData.singleUpdate);
  }

  handleError(error) {
    Framework.getEventManager().publish(AET_STORE_EVENTS.BULK_EDIT_COMPLETED, error);
  }
}
